/**
 * Typography hooks for dynamic scaling and accessibility support
 */

import { useState, useEffect, useCallback } from 'react';
import { Dimensions, AccessibilityInfo, Platform } from 'react-native';
import {
  getDeviceType,
  getResponsiveFontSize,
  getResponsiveLineHeight,
  getTypographyStyle,
  shouldReduceMotion,
  DeviceType,
} from '../utils/typography';
import {
  FontSize,
  TypographyVariant,
  AccessibilityTextScale,
  typographyTokens,
} from '../tokens/typography';

/**
 * Hook for responsive typography that adapts to screen size changes
 */
export function useResponsiveTypography() {
  const [deviceType, setDeviceType] = useState<DeviceType>(getDeviceType());

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', () => {
      setDeviceType(getDeviceType());
    });

    return () => subscription?.remove();
  }, []);

  const getScaledFontSize = useCallback(
    (
      fontSize: FontSize,
      accessibilityScale: AccessibilityTextScale = 'base'
    ) => {
      return getResponsiveFontSize(fontSize, deviceType, accessibilityScale);
    },
    [deviceType]
  );

  const getScaledLineHeight = useCallback(
    (
      fontSize: FontSize,
      accessibilityScale: AccessibilityTextScale = 'base'
    ) => {
      return getResponsiveLineHeight(fontSize, deviceType, accessibilityScale);
    },
    [deviceType]
  );

  const getVariantStyle = useCallback(
    (
      variant: TypographyVariant,
      accessibilityScale: AccessibilityTextScale = 'base'
    ) => {
      return getTypographyStyle(variant, deviceType, accessibilityScale);
    },
    [deviceType]
  );

  return {
    deviceType,
    getScaledFontSize,
    getScaledLineHeight,
    getVariantStyle,
  };
}

/**
 * Hook for accessibility-aware typography
 */
export function useAccessibleTypography() {
  const [accessibilityScale, setAccessibilityScale] =
    useState<AccessibilityTextScale>('base');
  const [reduceMotion, setReduceMotion] = useState(false);
  const [isScreenReaderEnabled, setIsScreenReaderEnabled] = useState(false);
  const [isBoldTextEnabled, setIsBoldTextEnabled] = useState(false);

  useEffect(() => {
    // Check initial accessibility settings
    const checkAccessibilitySettings = async () => {
      try {
        const [screenReader, boldText, reduceMotionEnabled] = await Promise.all(
          [
            AccessibilityInfo.isScreenReaderEnabled(),
            typeof (AccessibilityInfo as any).isBoldTextEnabled === 'function'
              ? (AccessibilityInfo as any).isBoldTextEnabled()
              : Promise.resolve(false),
            shouldReduceMotion(),
          ]
        );

        setIsScreenReaderEnabled(screenReader);
        setIsBoldTextEnabled(boldText);
        setReduceMotion(reduceMotionEnabled);

        // Adjust text scale based on accessibility needs
        if (boldText || screenReader) {
          setAccessibilityScale('lg');
        }
      } catch (error) {
        console.warn('Failed to check accessibility settings:', error);
      }
    };

    checkAccessibilitySettings();

    // Listen for accessibility changes
    const screenReaderSubscription = AccessibilityInfo.addEventListener(
      'screenReaderChanged',
      setIsScreenReaderEnabled
    );

    const boldTextSubscription =
      typeof (AccessibilityInfo as any).isBoldTextEnabled === 'function'
        ? AccessibilityInfo.addEventListener('boldTextChanged', enabled => {
            setIsBoldTextEnabled(enabled as boolean);
            setAccessibilityScale((enabled as boolean) ? 'lg' : 'base');
          })
        : undefined;

    const reduceMotionSubscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      setReduceMotion
    );

    return () => {
      screenReaderSubscription?.remove();
      boldTextSubscription?.remove?.();
      reduceMotionSubscription?.remove();
    };
  }, []);

  const adjustFontWeight = useCallback(
    (baseWeight: keyof typeof typographyTokens.fontWeight) => {
      if (isBoldTextEnabled) {
        const weights = Object.keys(typographyTokens.fontWeight);
        const currentIndex = weights.indexOf(baseWeight);
        const adjustedIndex = Math.min(currentIndex + 2, weights.length - 1);
        return weights[
          adjustedIndex
        ] as keyof typeof typographyTokens.fontWeight;
      }
      return baseWeight;
    },
    [isBoldTextEnabled]
  );

  return {
    accessibilityScale,
    setAccessibilityScale,
    reduceMotion,
    isScreenReaderEnabled,
    isBoldTextEnabled,
    adjustFontWeight,
  };
}

/**
 * Hook for dynamic text scaling based on user preferences
 */
export function useDynamicTextScale() {
  const [textScale, setTextScale] = useState<AccessibilityTextScale>('base');
  const { accessibilityScale } = useAccessibleTypography();
  const { getScaledFontSize, getScaledLineHeight } = useResponsiveTypography();

  // Use accessibility scale as default, but allow manual override
  const effectiveScale = textScale === 'base' ? accessibilityScale : textScale;

  const getScaledText = useCallback(
    (fontSize: FontSize) => {
      return {
        fontSize: getScaledFontSize(fontSize, effectiveScale),
        lineHeight: getScaledLineHeight(fontSize, effectiveScale),
      };
    },
    [getScaledFontSize, getScaledLineHeight, effectiveScale]
  );

  const scaleUp = useCallback(() => {
    const scales: AccessibilityTextScale[] = [
      'xs',
      'sm',
      'base',
      'lg',
      'xl',
      'xxl',
      'xxxl',
    ];
    const currentIndex = scales.indexOf(effectiveScale);
    if (currentIndex < scales.length - 1) {
      setTextScale(scales[currentIndex + 1]);
    }
  }, [effectiveScale]);

  const scaleDown = useCallback(() => {
    const scales: AccessibilityTextScale[] = [
      'xs',
      'sm',
      'base',
      'lg',
      'xl',
      'xxl',
      'xxxl',
    ];
    const currentIndex = scales.indexOf(effectiveScale);
    if (currentIndex > 0) {
      setTextScale(scales[currentIndex - 1]);
    }
  }, [effectiveScale]);

  const resetScale = useCallback(() => {
    setTextScale('base');
  }, []);

  return {
    textScale: effectiveScale,
    setTextScale,
    getScaledText,
    scaleUp,
    scaleDown,
    resetScale,
    canScaleUp: effectiveScale !== 'xxxl',
    canScaleDown: effectiveScale !== 'xs',
  };
}

/**
 * Hook for typography animation preferences
 */
export function useTypographyAnimation() {
  const { reduceMotion } = useAccessibleTypography();

  const getAnimationDuration = useCallback(
    (baseDuration: number) => {
      return reduceMotion ? 0 : baseDuration;
    },
    [reduceMotion]
  );

  const shouldAnimate = useCallback(() => {
    return !reduceMotion;
  }, [reduceMotion]);

  return {
    reduceMotion,
    getAnimationDuration,
    shouldAnimate,
  };
}
